import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Pills in the home page's shape language (operator, 27.09.2026): the red
// pill is the one main action on a screen, a white pill with a thin border is
// the secondary one, both in normal-case type. The former editorial style —
// square corners, letter-spaced capitals, navy fill — was rejected for the
// header button ("reads like a stamp") and made every form look like a
// different website than the home page.
const buttonVariants = cva(
  "group inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-coral-300 active:translate-y-0 disabled:opacity-50 disabled:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-coral-500 text-white shadow-[0_8px_18px_-8px_rgba(200,16,46,0.7)] hover:-translate-y-px hover:bg-coral-600",
        success:
          "bg-sage-700 text-white hover:-translate-y-px hover:bg-sage-800",
        secondary:
          "border border-navy-200 bg-white text-ink-900 hover:-translate-y-px hover:border-navy-400",
        // Kept for existing call sites; the same red pill as primary, since
        // red is spent on the one main action anyway.
        accent:
          "bg-coral-500 text-white shadow-[0_8px_18px_-8px_rgba(200,16,46,0.7)] hover:-translate-y-px hover:bg-coral-600",
        ghost:
          "text-navy-700 underline-offset-4 hover:text-coral-700 hover:underline",
        outline:
          "border border-navy-300 bg-white text-ink-900 hover:-translate-y-px hover:border-navy-600",
      },
      size: {
        sm: "min-h-9 px-4 py-1.5 text-[13px]",
        md: "min-h-11 px-6 py-2.5 text-[14px]",
        lg: "min-h-12 px-7 py-3 text-[15px]",
      },
      // A long label (the check button runs to 34 characters in Ukrainian)
      // may take a second line on a narrow phone instead of running off-screen.
      wrap: {
        false: "whitespace-nowrap",
        true: "max-w-full text-balance text-center leading-tight",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      wrap: false,
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, wrap, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size, wrap }), className)}
      {...props}
    />
  ),
);
Button.displayName = "Button";

export { buttonVariants };
